/**
 * NotificationToastDialog — Auto-show toast on SignalR push notification
 *
 * Matches Xamarin NotificationDialogViewModel:
 * - Shows task/trailer info from SignalR push data
 * - Auto-closes after 5 seconds (Xamarin Device.StartTimer(TimeSpan.FromSeconds(5), ...))
 * - Tap navigates to Dashboard (Task type) or Trailers page (Trailer type)
 * - Navigation blocked on BreakModePage or RestrictedAreaPage
 * - Description format:
 *   Move → "Move Task : The trailer {name} from {src} to {dst}"
 *   Audit with trailer → "Audit Task : Check if trailer {name} in {location}"
 *   Audit without → "Audit Task : Check if {location} is empty or not."
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@/constants';
import { SIGNALR_NOTIFICATION_EVENT } from '@/hooks/useSignalRNotifications';

import type { SignalRNotificationPayloadDTO } from '@/types';

/** Auto-close delay in ms — matches Xamarin TimeSpan.FromSeconds(5) */
const AUTO_CLOSE_DELAY = 5000;

/** Routes where navigation is blocked — matches Xamarin IsNotBreakAndRestrictedAreaPage */
const BLOCKED_ROUTES = [ROUTES.breakMode, ROUTES.restrictedArea];

/**
 * Build task description matching Xamarin NotificationDialogViewModel.SetNotificationDetails():
 * - Move → "Move Task : The trailer {name} from {src} to {dst}"
 * - Audit with trailer → "Audit Task : Check if trailer {name} in {location}"
 * - Audit without → "Audit Task : Check if {location} is empty or not."
 */
function buildDescription(
  payload: SignalRNotificationPayloadDTO,
  t: (key: string) => string
): string {
  const taskType = payload.data.task_type;
  const trailerName = payload.data.trailer_name;
  const moveSrc = payload.data.move_src_name;
  const moveDst = payload.data.move_dst_name;
  const auditLocation = payload.data.audit_locn_name;

  if (taskType === 'Move') {
    return `${t('notification.moveDescription')} ${trailerName} ${t('notification.from')} ${moveSrc} ${t('notification.to')} ${moveDst}`;
  }

  if (taskType === 'Audit' && trailerName) {
    return `${t('notification.auditDescriptionWithTrailer')}${trailerName} ${t('notification.in')} ${auditLocation}`;
  }

  if (taskType === 'Audit') {
    return `${t('notification.auditDescriptionWithoutTrailer')}${auditLocation}${t('notification.emptyOrNot')}`;
  }

  return `${t('notification.taskIdLabel')} ${payload.data.task_id}`;
}

export function NotificationToastDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [payload, setPayload] = useState<SignalRNotificationPayloadDTO | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const handleClose = useCallback(() => {
    clearTimer();
    setPayload(null);
  }, [clearTimer]);

  // Matches Xamarin NotificationDialogViewModel.NavigateToUpcomingTasksHandler
  const handleTap = useCallback(() => {
    clearTimer();

    const currentPath = location.pathname;
    const isBlocked = BLOCKED_ROUTES.some((route) => currentPath.startsWith(route));

    if (!isBlocked && payload) {
      // If task_type is present it's a Task notification → Dashboard; otherwise → Trailers
      const notifType = payload.notifications_type === 'trailer' ? 'Trailer' : 'Task';
      if (notifType === 'Trailer') {
        navigate(ROUTES.trailers);
      } else {
        navigate(ROUTES.dashboard);
      }
    }

    setPayload(null);
  }, [clearTimer, location.pathname, navigate, payload]);

  // Listen for SignalR push events
  useEffect(() => {
    const handleEvent = (e: Event) => {
      const customEvent = e as CustomEvent<SignalRNotificationPayloadDTO>;
      setPayload(customEvent.detail);
    };

    window.addEventListener(SIGNALR_NOTIFICATION_EVENT, handleEvent);
    return () => {
      window.removeEventListener(SIGNALR_NOTIFICATION_EVENT, handleEvent);
    };
  }, []);

  // Auto-close timer — matches Xamarin Device.StartTimer(TimeSpan.FromSeconds(5), ...)
  useEffect(() => {
    if (!payload) return;

    clearTimer();
    timerRef.current = setTimeout(() => {
      setPayload(null);
    }, AUTO_CLOSE_DELAY);

    return clearTimer;
  }, [payload, clearTimer]);

  if (!payload) return null;

  const description = buildDescription(payload, t);
  const taskId = payload.data.task_id;
  const taskType = payload.data.task_type;

  return (
    /* Matches Xamarin NotificationDialog overlay — slides in from top */
    <div className="fixed inset-x-0 top-0 z-[70] flex justify-center px-4 pt-4 animate-in slide-in-from-top duration-300">
      <button
        onClick={handleTap}
        className="w-full max-w-md rounded-lg border border-notification-border bg-white p-4 shadow-lg text-left"
      >
        {/* Header row: notification icon + title + close */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1357A0]">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
              </svg>
            </div>
            <span className="font-poppins text-sm font-semibold text-task-details">
              {t('notification.title')}
            </span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="font-poppins text-xs text-[#7F98B0]"
            aria-label={t('common.close')}
          >
            {t('common.close')}
          </button>
        </div>

        {/* Task info */}
        <div className="mt-2">
          {taskId && (
            <p className="font-poppins text-xs font-medium text-[#7F98B0]">
              {t('notification.taskIdLabel')} {taskId}
              {taskType ? ` - ${taskType}` : ''}
            </p>
          )}
          <p className="mt-1 font-poppins text-sm text-task-details">{description}</p>
        </div>

        {/* Auto-close progress indicator */}
        <div className="mt-3 h-[2px] w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full bg-[#1357A0] animate-shrink-width"
            style={{ animationDuration: `${String(AUTO_CLOSE_DELAY)}ms` }}
          />
        </div>
      </button>
    </div>
  );
}
